/**
 * Shared state for the "session timeout" prompt. While it's set, the modal owns
 * the session decision and readers stay out of the way.
 *
 * Stored as a timestamp, not a boolean, so a flag orphaned by a dead tab expires
 * instead of suppressing every automatic sign-out forever. Always go through
 * these helpers - a raw setItem of 'true' reads as stale and the guard silently
 * stops working.
 */
export const SESSION_TIMEOUT_PROMPT_KEY = 'session-timeout-prompt-active';

// The prompt lives for promptBeforeIdle (60s); slack for throttled timers.
const MAX_PROMPT_AGE_MS = 5 * 60 * 1000;

export const markSessionTimeoutPromptActive = (): void => {
  localStorage.setItem(SESSION_TIMEOUT_PROMPT_KEY, String(Date.now()));
};

export const clearSessionTimeoutPrompt = (): void => {
  localStorage.removeItem(SESSION_TIMEOUT_PROMPT_KEY);
};

export const isSessionTimeoutPromptActive = (): boolean => {
  const raw = localStorage.getItem(SESSION_TIMEOUT_PROMPT_KEY);

  if (!raw) {
    return false;
  }

  const markedAt = Number(raw);

  // Unparseable, including the legacy 'true' from earlier builds.
  if (!Number.isFinite(markedAt)) {
    clearSessionTimeoutPrompt();
    return false;
  }

  if (Date.now() - markedAt > MAX_PROMPT_AGE_MS) {
    clearSessionTimeoutPrompt();
    return false;
  }

  return true;
};
